function b64UrlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function b64UrlDecode(s: string): Uint8Array {
  const pad = s.length % 4 === 2 ? '==' : s.length % 4 === 3 ? '=' : '';
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function randBytes(n: number): Uint8Array {
  return window.crypto.getRandomValues(new Uint8Array(n));
}

function asArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export async function canUsePlatformPasskeys(): Promise<boolean> {
  try {
    if (typeof window === 'undefined') return false;
    if (!('PublicKeyCredential' in window)) return false;
    const pkc = window.PublicKeyCredential as typeof PublicKeyCredential | undefined;
    if (typeof pkc?.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') return false;
    return await pkc.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

export async function createPlatformPrfCredential(): Promise<{ credentialIdB64u: string }> {
  if (!('credentials' in navigator)) throw new Error('WebAuthn not available');
  const challenge = randBytes(32);
  const userId = randBytes(32);

  const publicKey: PublicKeyCredentialCreationOptions = {
    challenge: asArrayBuffer(challenge),
    rp: { name: 'eDosarMedical' },
    user: {
      id: asArrayBuffer(userId),
      name: 'eDosarMedical',
      displayName: 'eDosarMedical',
    },
    pubKeyCredParams: [{ type: 'public-key', alg: -7 }], // ES256
    authenticatorSelection: {
      authenticatorAttachment: 'platform',
      userVerification: 'required',
      residentKey: 'required',
    },
    timeout: 60000,
    extensions: { prf: {} as unknown } as AuthenticationExtensionsClientInputs,
  };

  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('Passkey creation cancelled');
  const rawId = new Uint8Array(cred.rawId);
  return { credentialIdB64u: b64UrlEncode(rawId) };
}

export async function evalPrfSecret(
  credentialIdB64u: string,
  salt: Uint8Array
): Promise<Uint8Array> {
  if (!('credentials' in navigator)) throw new Error('WebAuthn not available');
  const id = b64UrlDecode(credentialIdB64u);
  const challenge = randBytes(32);

  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: asArrayBuffer(challenge),
    allowCredentials: [{ id: asArrayBuffer(id), type: 'public-key' }],
    userVerification: 'required',
    timeout: 60000,
    extensions: { prf: { eval: { first: salt } } as unknown } as AuthenticationExtensionsClientInputs,
  };

  const assertion = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (!assertion) throw new Error('Passkey assertion cancelled');
  const ext = assertion.getClientExtensionResults?.() as AuthenticationExtensionsClientOutputs | undefined;
  const prf = (ext as unknown as { prf?: any })?.prf;
  const first: ArrayBuffer | undefined = prf?.results?.first;
  if (!first) throw new Error('PRF not available on this device/browser');
  return new Uint8Array(first);
}

